import { tap } from 'rxjs';
import { TuiSelectModule } from '@taiga-ui/kit';
import { TuiDataListModule, TuiLoaderModule, TuiTextfieldControllerModule } from '@taiga-ui/core';
import { TranslateModule } from '@ngx-translate/core';
import { Maybe } from '@iss/libs/graphql';
import { FormControl, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnInit,
  Output,
  Self,
} from '@angular/core';
import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';

import { Control, NOOP_VALUE_ACCESSOR } from '../forms';

import { SelectOptionDirective } from './select-option.directive';

@Component({
  selector: 'iss-select[ngModel],iss-select[formControlName],iss-select[formControl]',
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    NgFor,
    NgIf,
    NgTemplateOutlet,
    TranslateModule,
    TuiDataListModule,
    TuiLoaderModule,
    TuiSelectModule,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
  ],
})
export class SelectComponent<T, K = T> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() public items?: readonly T[] | null;
  @Input() public loading?: boolean | null;
  @Input() public readonly?: boolean | null;
  @Input() public identityFn: (item: T) => K = (item) => item as unknown as K;
  @Input() public textFn: (item: T) => string = (item) => `${ String(item) }`;
  @Input() public placeholder: Maybe<string>;
  @Output() public readonly selected = new EventEmitter<Maybe<K>>;

  @ContentChild(SelectOptionDirective) selectOption?: SelectOptionDirective<T>;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl<Maybe<K>>>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  public ngOnInit(): void {
    this.control.control.valueChanges.pipe(
      tap(value => this.selected.emit(value)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }
}
